// ©2024 Austin App House. All rights reserved.
import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  isMobileNav,
  MOBILE_NAV_MQ,
  getSidebarPinned,
  setSidebarPinned,
} from './dashboardNav';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import TaxInfoButton from '../../sidebar/taxInfoButton';
import BankInfoButton from '../../sidebar/bankInfoButton';
import { logoutUser } from '../../../storage/actions/authActions';
import { useIntercom } from 'react-use-intercom';
import DashboardIcon from '../../dashboard/icons/DashboardIcon';

const SidebarLink = ({
  to, icon, label, pathMatch, isActive, asButton, onButtonClick,
}) => {
  const active = isActive(pathMatch);
  const className = `sidebar-link${active ? ' active' : ''}`;
  const inner = (
    <>
      <span className="sidebar-icon">
        <DashboardIcon name={icon} />
      </span>
      <span className="sidebar-label">{label}</span>
    </>
  );

  if (asButton) {
    return (
      <button
        type="button"
        className={className}
        title={label}
        onClick={onButtonClick}
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={to}
      className={className}
      aria-current={active ? 'page' : undefined}
      title={label}
    >
      {inner}
    </Link>
  );
};

const Sidebar = ({
  isNavOpen, setNavOpen, auth, profile, logoutUser, location,
}) => {
  const sidebarRef = useRef(null);
  const [isPinned, setIsPinned] = useState(getSidebarPinned);
  const closeNav = useCallback(() => setNavOpen(false), [setNavOpen]);
  const { shutdown } = useIntercom();

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.setAttribute('aria-expanded', isNavOpen ? 'true' : 'false');
    }
  }, [isNavOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeNav();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeNav]);

  useEffect(() => {
    const mobileMq = window.matchMedia(MOBILE_NAV_MQ);
    const onMqChange = () => {
      if (!mobileMq.matches) {
        closeNav();
        setIsPinned(getSidebarPinned());
      } else {
        setIsPinned(false);
      }
    };
    mobileMq.addEventListener('change', onMqChange);
    return () => mobileMq.removeEventListener('change', onMqChange);
  }, [closeNav]);

  useEffect(() => {
    closeNav();

    const sidebar = sidebarRef.current;
    const active = document.activeElement;
    if (sidebar && active && sidebar.contains(active)) {
      active.blur();
    }
  }, [location.pathname, closeNav]);

  const onPinClick = (event) => {
    event.stopPropagation();
    if (isMobileNav()) return;
    setIsPinned((prev) => {
      const next = !prev;
      setSidebarPinned(next);
      return next;
    });
  };

  const onSidebarClick = (event) => {
    if (!isMobileNav()) return;
    event.stopPropagation();
    if (event.target.closest('.sidebar-pin')) return;

    const link = event.target.closest('a.sidebar-link');
    if (link) {
      if (isNavOpen) {
        closeNav();
      } else {
        event.preventDefault();
        setNavOpen(true);
      }
      return;
    }

    const actionBtn = event.target.closest('button.sidebar-link');
    if (actionBtn) {
      if (!isNavOpen) {
        event.preventDefault();
        setNavOpen(true);
      }
      return;
    }

    setNavOpen(!isNavOpen);
  };

  const logOut = () => {
    logoutUser(shutdown);
  };

  const isActive = (path) => window.location.pathname.includes(path);
  const userType = auth.user.userType;
  const isClient = userType === 'client';
  const isContractor = userType === 'contractor';
  const isSuperAdmin = userType === 'superAdmin';
  const canManageBilling = profile.clientUser?.accessctrl !== 'Manager'
    && profile.clientUser?.accessctrl !== 'Dispatcher';
  const dashboardPath = isContractor ? '/dashboard/upcoming-work' : '/dashboard/assigned';
  const usesDashboardNav = isClient || isContractor || isSuperAdmin;

  return (
    <aside
      className={`dashboard-sidebar${isSuperAdmin ? ' dashboard-sidebar--superadmin' : ''}`}
      id="dashboard-sidebar-nav"
      data-pinned={isPinned ? 'true' : 'false'}
      role="navigation"
      aria-label="Dashboard navigation"
      aria-expanded={isNavOpen}
      ref={sidebarRef}
      onClick={onSidebarClick}
    >
      <Link
        to={dashboardPath}
        className="sidebar-brand"
        aria-label="Wurqly Home"
      >
        <img
          className="sidebar-brand-mark"
          src="/assets/wurqly-wordmark-cobalt.svg"
          alt="Wurqly"
          width={40}
          height={40}
        />
        <img
          className="sidebar-brand-full"
          src="/wurqly-wordmark-cobalt.png"
          alt="Wurqly"
          width={160}
          height={40}
        />
      </Link>

      <button
        className="sidebar-pin"
        type="button"
        aria-label="Pin sidebar"
        aria-pressed={isPinned}
        onClick={onPinClick}
      >
        <DashboardIcon name="pin" size={18} className="sidebar-pin-icon" />
        <span className="sidebar-label">Pin sidebar</span>
      </button>

      <nav className="sidebar-nav" aria-label="Dashboard navigation">
        <SidebarLink
          to={dashboardPath}
          icon={usesDashboardNav ? 'grid' : 'workOrders'}
          label={usesDashboardNav ? 'Dashboard' : 'Work Orders'}
          pathMatch="/dashboard"
          isActive={isActive}
        />

        {isClient ? (
          <>
            <SidebarLink
              to="/find-contractor"
              icon="search"
              label="Find Contractor"
              pathMatch="/find-contractor"
              isActive={isActive}
            />
            <SidebarLink
              to="/contractor-groups"
              icon="contractors"
              label="Preferred"
              pathMatch="/contractor-groups"
              isActive={isActive}
            />
            <SidebarLink
              to="/manage-custom-fields"
              icon="customFields"
              label="Custom Fields"
              pathMatch="/manage-custom-fields"
              isActive={isActive}
            />
            <SidebarLink
              to="/manage-teams"
              icon="teams"
              label="Manage Teams"
              pathMatch="/manage-teams"
              isActive={isActive}
            />
            {canManageBilling ? (
              <>
                <SidebarLink
                  to="/user-management"
                  icon="users"
                  label="User Management"
                  pathMatch="/user-management"
                  isActive={isActive}
                />
                <SidebarLink
                  to="/manage-funds"
                  icon="transactions"
                  label="Manage Funds"
                  pathMatch="/manage-funds"
                  isActive={isActive}
                />
                <SidebarLink
                  to="/payment-methods"
                  icon="payment"
                  label="Payment Methods"
                  pathMatch="/payment-methods"
                  isActive={isActive}
                />
              </>
            ) : null}
            <SidebarLink
              to="/templates"
              icon="templates"
              label="Templates"
              pathMatch="/templates"
              isActive={isActive}
            />
          </>
        ) : null}

        {isContractor ? (
          <SidebarLink
            to="/manage-funds"
            icon="payments"
            label="Payment History"
            pathMatch="/manage-funds"
            isActive={isActive}
          />
        ) : null}

        {isSuperAdmin ? (
          <>
            <SidebarLink
              to="/certificates-contractors"
              icon="document"
              label="Certification"
              pathMatch="/certificates-contractors"
              isActive={isActive}
            />
            <SidebarLink
              to="/contractors"
              icon="users"
              label="Contractors"
              pathMatch="/contractors"
              isActive={isActive}
            />
            <SidebarLink
              to="/recruits"
              icon="teams"
              label="Recruits"
              pathMatch="/recruits"
              isActive={isActive}
            />
            <SidebarLink
              to="/manage-companies"
              icon="edit"
              label="Manage Companies"
              pathMatch="/manage-companies"
              isActive={isActive}
            />
            <SidebarLink
              to="/manage-funds"
              icon="payments"
              label="Manage Funds"
              pathMatch="/manage-funds"
              isActive={isActive}
            />
            <SidebarLink
              to="/funding-sources"
              icon="pieChart"
              label="Funding Sources"
              pathMatch="/funding-sources"
              isActive={isActive}
            />
            <SidebarLink
              to="/platform-fee-transactions"
              icon="payment"
              label="Platform Fee Transactions"
              pathMatch="/platform-fee-transactions"
              isActive={isActive}
            />
            <div className="sidebar-export-group">
              <TaxInfoButton />
              <BankInfoButton />
            </div>
          </>
        ) : null}
      </nav>

      <div className="sidebar-bottom">
        <SidebarLink
          asButton
          icon="signOut"
          label="Log out"
          pathMatch=""
          isActive={() => false}
          onButtonClick={logOut}
        />
      </div>
    </aside>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  profile: state.profile,
});

const mapDispatchToProps = {
  logoutUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Sidebar));
